import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bell, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../shared/session';
import { usePush } from '../../shared/use-push';

/**
 * De vraag om meldingen aan te zetten, als smalle balk.
 *
 * Tot UI-2 stond hier een kaart van een half scherm: een titel, drie regels uitleg, een zin over
 * wat er in een melding staat en een knop. Boven de tijdlijn. Het gevolg was dat het eerste wat
 * een medewerker zag niet zijn berichten waren maar een verzoek — en dat elke keer opnieuw, want
 * de kaart bleef staan tot hij ja zei.
 *
 * Nu: één regel, één knop, en een kruisje. Drie keuzes zitten erin:
 *
 * - **Wegklikbaar.** Wie nee zegt, hoort niet elke dag opnieuw gevraagd te worden; dat is hoe een
 *   verzoek een irritatie wordt en daarna genegeerd. De keuze staat in `localStorage`: per toestel,
 *   want de vraag gaat ook over dit toestel.
 * - **Geen uitleg in de balk.** Wat er in een melding staat en wat niet, hoort bij de instellingen
 *   (`/app/settings`), waar iemand het leest omdat hij het wil weten. In een balk leest niemand
 *   het — en het past er ook niet in: de eerste versie zei "Meldingen staan uit. Zet ze aan en je
 *   weet het meteen", dat liep over drie regels en was daarmee weer een kaart. Nu staat er wat er
 *   aan de hand is, en de knop zegt wat je eraan doet.
 * - **Op een iPhone wordt het een link, geen knop.** Web push werkt daar alleen vanaf het
 *   beginscherm; een knop die niets kan doen is erger dan geen knop.
 */
const DISMISSED_KEY = 'up2d8.push-prompt.dismissed';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Privémodus of geblokkeerde opslag: dan tonen we de balk gewoon.
    return false;
  }
}

export function PushPrompt() {
  const { t } = useTranslation();
  const push = usePush();
  const queryClient = useQueryClient();
  const { profile } = useSession();
  const [dismissed, setDismissed] = useState(wasDismissed);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Niets te doen: dan komt de balk de volgende keer terug, en dat is de minst erge uitkomst.
    }
  };

  const enable = async () => {
    await push.enable();
    await queryClient.invalidateQueries({ queryKey: ['push-subscriptions'] });
  };

  // Staat het aan, of is er geen sessie, of klikte hij het weg: geen balk.
  if (!profile || dismissed || push.state === 'on' || push.state === 'unsupported') return null;

  return (
    <div
      data-testid="push-prompt"
      className="flex items-center gap-inline rounded-md border border-border-light bg-surface px-element py-2 shadow-sm"
    >
      <Bell aria-hidden className="h-5 w-5 shrink-0 text-brand-primary" />
      <p className="min-w-0 flex-1 text-support text-text-secondary">{t('push.promptShort')}</p>

      {push.state === 'ios-install-needed' ? (
        <Link
          to="/app/install"
          className="shrink-0 font-heading text-support font-semibold text-brand-primary"
        >
          {t('push.promptHow')}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => void enable()}
          className="shrink-0 font-heading text-support font-semibold text-brand-primary"
        >
          {t('push.promptEnable')}
        </button>
      )}

      <button
        type="button"
        onClick={dismiss}
        aria-label={t('common.close')}
        className="shrink-0 rounded-pill p-1 text-text-muted hover:text-text-primary"
      >
        <X aria-hidden className="h-4 w-4" />
      </button>
    </div>
  );
}
